import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import DateInput from "../DateInput";
import { isoToEU } from "../../lib/format";
import { useSafeFetch } from "../../lib/useSafeFetch";
import { useConfirm } from "../ConfirmDialog";
import { Save, Settings2, X as XIcon, Plus, Trash2, AlertTriangle } from "lucide-react";

/**
 * Seasonal Rates editor for a Car Group. Renders one input per configured
 * global season and lets the admin set a daily rate for each.
 *
 *   value = [{ season_id, daily_rate }, ...]
 *
 * Empty value or empty seasons list → the vehicle uses `daily_rate` as
 * the fallback rate for all dates.
 */
export default function SeasonalRatesEditor({ value = [], onChange, fallbackDailyRate }) {
  const { data: seasons, loading } = useSafeFetch("/settings/seasons", {
    defaultValue: [], asList: true,
  });

  // Season-id → { daily_rate, duration_tiers[] } so we can preserve tiers
  // even when the admin blanks the plain daily rate (tiers can stand alone).
  const rowMap = useMemo(() => {
    const m = {};
    for (const r of value || []) {
      if (r?.season_id) {
        m[r.season_id] = {
          daily_rate:     Number(r.daily_rate) || 0,
          duration_tiers: Array.isArray(r.duration_tiers) ? r.duration_tiers : [],
        };
      }
    }
    return m;
  }, [value]);

  const [tierModalSeason, setTierModalSeason] = useState(null); // { id, name }

  if (loading) {
    return <p className="text-xs text-neutral-500">Loading seasons…</p>;
  }

  if (seasons.length === 0) {
    return (
      <p className="text-xs text-neutral-500 dark:text-neutral-400 italic border border-dashed border-black/10 dark:border-white/10 p-3 rounded">
        No seasons are configured yet. Add them under Business Settings → Seasons, then come back to set per-season rates.
      </p>
    );
  }

  const emit = (nextMap) => {
    // Only emit rows that carry data (rate > 0 OR at least one tier).
    const next = seasons
      .map((s) => ({
        season_id:      s.id,
        daily_rate:     nextMap[s.id]?.daily_rate || 0,
        duration_tiers: nextMap[s.id]?.duration_tiers || [],
      }))
      .filter((r) => r.daily_rate > 0 || (r.duration_tiers || []).length > 0);
    onChange(next);
  };

  const handleRateChange = (season_id, rate) => {
    const numeric = Number(rate);
    const nextMap = { ...rowMap };
    nextMap[season_id] = {
      daily_rate:     Number.isFinite(numeric) ? numeric : 0,
      duration_tiers: nextMap[season_id]?.duration_tiers || [],
    };
    emit(nextMap);
  };

  const handleTiersSave = (season_id, tiers) => {
    const nextMap = { ...rowMap };
    nextMap[season_id] = {
      daily_rate:     nextMap[season_id]?.daily_rate || 0,
      duration_tiers: tiers,
    };
    emit(nextMap);
    setTierModalSeason(null);
  };

  return (
    <div className="w-full max-w-full space-y-2" data-testid="seasonal-rates-editor">
      {/* Header row — uses the same flex + fixed widths as the data
          rows so labels line up 1:1 with the columns below. */}
      <div className="w-full max-w-full flex items-center gap-6 box-border text-[10px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">
        <span className="min-w-[190px] w-[200px] shrink-0">Season</span>
        <span className="min-w-[210px] w-[220px] shrink-0">Range</span>
        <span className="w-[160px] shrink-0 pr-1">Daily rate (€)</span>
        <span className="min-w-[140px] shrink-0 ml-auto text-right pr-2 whitespace-nowrap">Tiers</span>
      </div>
      {seasons.map((s) => {
        const tiers = rowMap[s.id]?.duration_tiers || [];
        return (
          <div
            key={s.id}
            className="w-full max-w-full flex items-center gap-6 box-border"
            data-testid={`seasonal-rate-row-${s.id}`}
          >
            <div className="min-w-[190px] w-[200px] shrink-0">
              <p className="text-sm font-medium truncate">{s.name}</p>
              {tiers.length > 0 && (
                <p
                  className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-0.5 truncate"
                  data-testid={`seasonal-tier-badge-${s.id}`}
                >
                  {tiers.length} duration tier{tiers.length > 1 ? "s" : ""} active
                </p>
              )}
            </div>
            <p className="min-w-[210px] w-[220px] shrink-0 text-sm text-neutral-500 dark:text-neutral-400 tabular-nums whitespace-nowrap truncate">
              {isoToEU(s.start_date)} → {isoToEU(s.end_date)}
            </p>
            <div className="w-[160px] shrink-0">
              <input
                type="number"
                step="0.01"
                min={0}
                value={(() => {
                  const stored = rowMap[s.id]?.daily_rate;
                  if (stored === undefined || stored === 0) return "";
                  return String(stored);
                })()}
                onChange={(e) => handleRateChange(s.id, e.target.value)}
                placeholder={
                  tiers.length > 0
                    ? "Rate covered by tiers"
                    : fallbackDailyRate
                      ? `default ${fallbackDailyRate}`
                      : "0.00"
                }
                className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 px-1 outline-none focus:border-black dark:focus:border-white text-xs text-right"
                data-testid={`seasonal-rate-input-${s.id}`}
              />
            </div>
            <div className="min-w-[140px] shrink-0 ml-auto flex justify-end pr-2">
              <button
                type="button"
                onClick={() => setTierModalSeason(s)}
                className={`mr-0 text-[11px] px-2 py-1.5 rounded border inline-flex items-center gap-1 whitespace-nowrap transition-colors ${tiers.length > 0
                  ? "border-emerald-600 text-emerald-700 dark:text-emerald-300 dark:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                  : "border-black/15 dark:border-white/15 text-neutral-700 dark:text-neutral-300 hover:border-black dark:hover:border-white"}`}
                data-testid={`open-tiers-${s.id}`}
              >
                <Settings2 size={13} /> Edit tiers
              </button>
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-2">
        Leave a season blank to fall back to the standard daily rate. Duration tiers, when set, override the season&apos;s daily rate for rentals whose length falls inside a tier window.
      </p>

      {tierModalSeason && (
        <DurationTiersModal
          season={tierModalSeason}
          initialTiers={rowMap[tierModalSeason.id]?.duration_tiers || []}
          fallbackDailyRate={fallbackDailyRate}
          seasonDailyRate={rowMap[tierModalSeason.id]?.daily_rate || 0}
          onClose={() => setTierModalSeason(null)}
          onSave={(tiers) => handleTiersSave(tierModalSeason.id, tiers)}
        />
      )}
    </div>
  );
}


/**
 * DurationTiersModal — dedicated dialog to CRUD `duration_tiers[]` on one
 * season row inside a vehicle. Validates min ≤ max and disallows overlaps
 * before letting the admin save.
 */
function DurationTiersModal({
  season, initialTiers = [], fallbackDailyRate, seasonDailyRate, onClose, onSave,
}) {
  const [tiers, setTiers] = useState(
    () => (initialTiers || []).map((t) => ({
      min_days:    String(t.min_days ?? ""),
      max_days:    String(t.max_days ?? ""),
      daily_rate:  String(t.daily_rate ?? ""),
    })),
  );
  const [error, setError] = useState("");

  const addTier = () => {
    setTiers((prev) => [...prev, { min_days: "", max_days: "", daily_rate: "" }]);
    setError("");
  };
  const removeTier = (idx) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
    setError("");
  };
  const updateTier = (idx, field, val) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: val } : t)));
    setError("");
  };

  const validateAndSave = () => {
    // Parse
    const parsed = tiers.map((t, i) => {
      const lo = Number(t.min_days);
      const hi = Number(t.max_days);
      const rate = Number(t.daily_rate);
      return { i, lo, hi, rate };
    });

    for (const p of parsed) {
      if (!Number.isFinite(p.lo) || !Number.isFinite(p.hi) || !Number.isFinite(p.rate)) {
        setError(`Row ${p.i + 1}: all fields must be numbers.`);
        return;
      }
      if (p.lo < 1) return setError(`Row ${p.i + 1}: 'From days' must be ≥ 1.`);
      if (p.hi < p.lo) return setError(`Row ${p.i + 1}: 'To days' must be ≥ 'From days'.`);
      if (p.rate <= 0) return setError(`Row ${p.i + 1}: daily rate must be > 0.`);
    }

    // Overlap detection — sort by lo, ensure each next.lo > prev.hi
    const sorted = [...parsed].sort((a, b) => a.lo - b.lo);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].lo <= sorted[i - 1].hi) {
        setError(
          `Tiers overlap: rows for ${sorted[i - 1].lo}-${sorted[i - 1].hi} and ${sorted[i].lo}-${sorted[i].hi} days.`,
        );
        return;
      }
    }

    const cleaned = parsed
      .sort((a, b) => a.lo - b.lo)
      .map((p) => ({
        min_days:   Math.floor(p.lo),
        max_days:   Math.floor(p.hi),
        daily_rate: Math.round(p.rate * 100) / 100,
      }));
    onSave(cleaned);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="duration-tiers-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto rounded border border-black/10 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
              Manage seasonal duration tiers
            </p>
            <h2 className="font-display text-3xl mt-2">
              <em className="italic">{season.name}</em>
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2 max-w-xl">
              Charge a different daily rate depending on how many days the customer rents.
              Example: 1-1 day → €50, 2-2 → €49, 3-6 → €43, 7-14 → €39, 15-30 → €35.
              When no tier matches the rental length, price falls back to the vehicle&apos;s
              default daily rate (€{Number(fallbackDailyRate || 0).toFixed(2)}).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-black dark:hover:text-white p-1"
            data-testid="tiers-close"
          >
            <XIcon size={20} />
          </button>
        </div>

        {seasonDailyRate > 0 && tiers.length > 0 && (
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 rounded">
            Note: this season&apos;s plain daily rate (€{seasonDailyRate.toFixed(2)}) is currently
            <strong> ignored</strong> whenever any tier below matches the rental length.
          </p>
        )}

        <div className="border border-black/10 dark:border-white/10 rounded overflow-hidden">
          <div className="grid grid-cols-[100px_100px_1fr_60px] gap-3 items-center bg-[#FAF9F6] dark:bg-[#111827] border-b border-black/8 dark:border-white/10 px-4 py-3">
            <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">From days</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">To days</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">Daily rate (€)</span>
            <span></span>
          </div>
          {tiers.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400 italic">
              No duration tiers yet — rentals price at the season&apos;s plain daily rate (or the vehicle default).
            </div>
          )}
          {tiers.map((t, i) => (
            <div
              key={i}
              className="grid grid-cols-[100px_100px_1fr_60px] gap-3 items-center px-4 py-2 border-b border-black/5 dark:border-white/5"
              data-testid={`tier-row-${i}`}
            >
              <input
                type="number" min={1} step={1}
                value={t.min_days}
                onChange={(e) => updateTier(i, "min_days", e.target.value)}
                className="bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm"
                data-testid={`tier-min-${i}`}
              />
              <input
                type="number" min={1} step={1}
                value={t.max_days}
                onChange={(e) => updateTier(i, "max_days", e.target.value)}
                className="bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm"
                data-testid={`tier-max-${i}`}
              />
              <input
                type="number" min={0} step={0.01}
                value={t.daily_rate}
                onChange={(e) => updateTier(i, "daily_rate", e.target.value)}
                className="bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm"
                placeholder="0.00"
                data-testid={`tier-rate-${i}`}
              />
              <button
                type="button"
                onClick={() => removeTier(i)}
                className="text-rose-600 dark:text-rose-400 p-1 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded justify-self-end"
                data-testid={`tier-remove-${i}`}
                aria-label={`Remove tier row ${i + 1}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addTier}
          className="mt-3 text-xs px-3 py-1.5 border border-dashed border-black/25 dark:border-white/25 hover:border-black dark:hover:border-white rounded inline-flex items-center gap-1"
          data-testid="tier-add"
        >
          <Plus size={13} /> Add tier
        </button>

        {tiers.length === 0 && (
          <button
            type="button"
            onClick={() => {
              const base = Number(fallbackDailyRate) || Number(seasonDailyRate) || 100;
              setTiers([
                { min_days: "1",  max_days: "3",  daily_rate: (base * 1.10).toFixed(2) },
                { min_days: "4",  max_days: "7",  daily_rate: base.toFixed(2) },
                { min_days: "8",  max_days: "14", daily_rate: (base * 0.85).toFixed(2) },
                { min_days: "15", max_days: "30", daily_rate: (base * 0.75).toFixed(2) },
              ]);
              setError("");
            }}
            className="mt-3 ml-2 text-xs px-3 py-1.5 border border-emerald-500/50 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded inline-flex items-center gap-1"
            data-testid="tier-load-defaults"
          >
            Load default windows (1-3 · 4-7 · 8-14 · 15-30)
          </button>
        )}

        {error && (
          <p
            className="text-xs text-rose-600 dark:text-rose-400 mt-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 p-2 rounded"
            data-testid="tiers-error"
          >
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="border border-black/15 dark:border-white/15 px-6 py-2.5 text-sm rounded"
            data-testid="tiers-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={validateAndSave}
            className="bg-black text-white dark:bg-white dark:text-black px-6 py-2.5 text-sm rounded inline-flex items-center gap-2"
            data-testid="tiers-save"
          >
            <Save size={13} /> Save tiers
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Fleet inventory (physical vehicles) admin page — new /admin/car-fleet route.
 */

const EMPTY_FORM = {
  car_group_id: "",
  license_plate: "",
  location_ids: [],
  insurance_expiry: "",
  kteo_expiry: "",
  next_service_km: 0,
  next_service_date: "",
  mileage_km: 0,
  status: "Active",
  image_data: "",
  notes: "",
};

// v4.7.0 — physical asset states. Anything other than "Active" pulls the unit
// out of its Car Group's rental availability pool.
const STATUSES = [
  { value: "Active", label: "Active (available for rent)" },
  { value: "At Service", label: "At Service (scheduled service)" },
  { value: "At Maintenance", label: "At Maintenance (repairs / workshop)" },
  { value: "Inactive / For Sale", label: "Inactive / For Sale (decommissioned)" },
];

// Legacy values stored before v4.7.0.
const LEGACY_STATUS = { "In Service": "At Service", "Sales": "Inactive / For Sale", "Inactive": "Inactive / For Sale" };

export function AdminCarFleet() {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fleetQ = useSafeFetch("/admin/fleet", { defaultValue: [], asList: true });
  const groupsQ = useSafeFetch("/vehicles", { defaultValue: [], asList: true });
  const locationsQ = useSafeFetch("/settings/locations", { defaultValue: [], asList: true });

  const items = fleetQ.data;
  const groups = groupsQ.data;
  const locations = locationsQ.data;
  const loading = fleetQ.loading || groupsQ.loading || locationsQ.loading;
  const loadError = fleetQ.error || groupsQ.error || locationsQ.error;

  // Whenever any bulk data failed to load, surface it once as a toast (the
  // hook already keeps the UI safe with empty lists).
  useEffect(() => {
    if (loadError) {
      toast.error("Failed to load fleet data", { description: loadError, duration: 6000 });
    }
  }, [loadError]);

  const load = () => {
    fleetQ.refetch();
    groupsQ.refetch();
    locationsQ.refetch();
  };

  const { confirm, dialog: confirmDialog } = useConfirm();

  const openCreate = () => {
    setEditing("new");
    setForm({ ...EMPTY_FORM, car_group_id: groups[0]?.id || "" });
  };

  const openEdit = (fv) => {
    setEditing(fv.id);
    setForm({
      car_group_id: fv.car_group_id,
      license_plate: fv.license_plate,
      location_ids: fv.location_ids || [],
      insurance_expiry: fv.insurance_expiry || "",
      kteo_expiry: fv.kteo_expiry || "",
      next_service_km: fv.next_service_km ?? 0,
      next_service_date: fv.next_service_date || "",
      mileage_km: fv.mileage_km ?? 0,
      status: LEGACY_STATUS[fv.status] || fv.status || "Active",
      image_data: fv.image_data || "",
      notes: fv.notes || "",
    });
  };

  const close = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.car_group_id) {
      toast.warning("Please select a car group first.");
      return;
    }
    if (!form.license_plate.trim()) {
      toast.warning("License plate is required.");
      return;
    }
    const payload = {
      ...form,
      license_plate: form.license_plate.trim().toUpperCase(),
      mileage_km: Number(form.mileage_km) || 0,
      next_service_km: Number(form.next_service_km) || 0,
      next_service_date: form.next_service_date || null,
      insurance_expiry: form.insurance_expiry || null,
      kteo_expiry: form.kteo_expiry || null,
      image_data: form.image_data || null,
    };
    try {
      if (editing === "new") await api.post("/admin/fleet", payload);
      else await api.put(`/admin/fleet/${editing}`, payload);
      toast.success("Fleet vehicle saved");
      close();
      load();
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error("Save failed", { description: msg, duration: 6000 });
    }
  };

  const remove = async (fv) => {
    const ok = await confirm({
      title:        "Delete fleet vehicle",
      message:      "Removes only this individual unit — the Car Group config stays intact for the rest of the fleet.",
      subject:      `${fv.license_plate} · ${fv.car_group_id?.slice(0, 8) || "—"}`,
      confirmLabel: "Delete vehicle",
      testId:       "confirm-delete-fleet-vehicle",
    });
    if (!ok) return;
    await api.delete(`/admin/fleet/${fv.id}`);
    load();
  };

  const readFile = (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.warning("Image too large", { description: "Please upload a file smaller than 500 KB." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_data: reader.result }));
    reader.readAsDataURL(file);
  };

  const toggleLocation = (id) => {
    setForm((f) => ({
      ...f,
      location_ids: f.location_ids.includes(id)
        ? f.location_ids.filter((x) => x !== id)
        : [...f.location_ids, id],
    }));
  };

  const groupsById = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const locsById = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);

  return (
    <div data-testid="admin-car-fleet-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Inventory</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Car <em className="italic">fleet</em>.
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-xl">
            Physical vehicles in your fleet. Each unit inherits pricing from its Car Group.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-black text-white dark:bg-white dark:text-black px-5 py-2.5 text-sm hover:opacity-90 transition-opacity rounded"
          data-testid="add-fleet-vehicle"
        >
          + Add fleet vehicle
        </button>
      </div>

      <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]">
            <tr>
              <th className="py-3 px-4">Plate</th>
              <th className="py-3 px-4">Car group</th>
              <th className="py-3 px-4">Locations</th>
              <th className="py-3 px-4">Mileage</th>
              <th className="py-3 px-4">Insurance</th>
              <th className="py-3 px-4">KTEO</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="py-8 text-center text-neutral-500">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-neutral-500">No fleet vehicles yet.</td></tr>
            )}
            {!loading && items.map((fv) => {
              const g = groupsById[fv.car_group_id];
              const locNames = (fv.location_ids || []).length === 0
                ? "All locations"
                : (fv.location_ids || []).map((id) => locsById[id]?.name).filter(Boolean).join(", ");
              return (
                <tr key={fv.id} className="border-b border-black/5 dark:border-white/5" data-testid={`fleet-vehicle-row-${fv.license_plate}`}>
                  <td className="py-3 px-4 font-mono">{fv.license_plate}</td>
                  <td className="py-3 px-4">{g ? `${g.brand} ${g.name}` : <span className="text-rose-500">Unknown group</span>}</td>
                  <td className="py-3 px-4 text-xs">{locNames}</td>
                  <td className="py-3 px-4 tabular-nums">{fv.mileage_km?.toLocaleString?.() || 0} km</td>
                  <td className="py-3 px-4 text-xs">{isoToEU(fv.insurance_expiry) || "—"}</td>
                  <td className="py-3 px-4 text-xs">{isoToEU(fv.kteo_expiry) || "—"}</td>
                  <td className="py-3 px-4">
                    <StatusPill status={fv.status} />
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(fv)} className="text-xs px-2 py-1 border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white rounded" data-testid={`edit-fleet-${fv.license_plate}`}>Edit</button>
                    <button onClick={() => remove(fv)} className="text-xs px-2 py-1 border border-black/15 dark:border-white/15 hover:border-rose-500 text-rose-600 dark:text-rose-400 rounded ml-1" data-testid={`delete-fleet-${fv.license_plate}`}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={close}>
          <div
            className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto rounded border border-black/10 dark:border-white/10"
            onClick={(e) => e.stopPropagation()}
            data-testid="fleet-vehicle-form-modal"
          >
            <h2 className="font-display text-3xl mb-6" data-testid="fv-modal-title">
              {editing === "new" ? "Add Vehicle" : `Edit Vehicle · ${form.license_plate}`}
            </h2>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                <FieldLabel label="Car group *">
                  <select
                    required
                    value={form.car_group_id}
                    onChange={(e) => setForm({ ...form, car_group_id: e.target.value })}
                    className="editorial-input w-full"
                    data-testid="fv-car-group"
                    disabled={!Array.isArray(groups) || groups.length === 0}
                  >
                    <option value="">
                      {(!Array.isArray(groups) || groups.length === 0)
                        ? "No car groups available — create one first"
                        : "Select…"}
                    </option>
                    {(Array.isArray(groups) ? groups : []).map((g) => (
                      <option key={g.id} value={g.id}>{g.brand} {g.name}</option>
                    ))}
                  </select>
                  {(!Array.isArray(groups) || groups.length === 0) && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Please create a Car Group under <strong>Vehicles</strong> before adding fleet inventory.
                    </p>
                  )}
                </FieldLabel>
                <FieldLabel label="License plate *">
                  <input required value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value.toUpperCase() })} className="editorial-input w-full font-mono" data-testid="fv-plate" />
                </FieldLabel>
                <FieldLabel label="Current odometer (km)">
                  <input type="number" min={0} value={form.mileage_km} onChange={(e) => setForm({ ...form, mileage_km: e.target.value })} className="editorial-input w-full" data-testid="fv-mileage" />
                </FieldLabel>
                <FieldLabel label="Operational status">
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="editorial-input w-full"
                    data-testid="fv-status"
                  >
                    {STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </FieldLabel>
                <FieldLabel label="Next service at (km)">
                  <input type="number" min={0} value={form.next_service_km} onChange={(e) => setForm({ ...form, next_service_km: e.target.value })} className="editorial-input w-full" data-testid="fv-next-service-km" />
                </FieldLabel>
                <FieldLabel label="Next service date">
                  <DateInput value={form.next_service_date} onChange={(v) => setForm({ ...form, next_service_date: v })} testId="fv-next-service-date" />
                </FieldLabel>
                <FieldLabel label="Insurance expiry">
                  <DateInput value={form.insurance_expiry} onChange={(v) => setForm({ ...form, insurance_expiry: v })} testId="fv-insurance" />
                </FieldLabel>
                <FieldLabel label="KTEO expiry">
                  <DateInput value={form.kteo_expiry} onChange={(v) => setForm({ ...form, kteo_expiry: v })} testId="fv-kteo" />
                </FieldLabel>
              </div>

              {form.status !== "Active" && (
                <div
                  className="flex gap-2.5 p-3.5 rounded border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/25 text-amber-900 dark:text-amber-200 text-sm"
                  data-testid="fv-status-warning"
                >
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <p>
                    <strong>Notice:</strong> Setting this vehicle to {form.status} immediately excludes it
                    from its Car Group&rsquo;s rental availability pool until set back to Active.
                  </p>
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-2">
                  Available at locations (empty = all)
                </p>
                <div className="flex flex-wrap gap-2">
                  {locations.map((l) => {
                    const on = form.location_ids.includes(l.id);
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => toggleLocation(l.id)}
                        className={`text-xs px-3 py-1.5 rounded border transition-colors ${on
                          ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                          : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white text-neutral-700 dark:text-neutral-300"}`}
                        data-testid={`fv-loc-${l.name}`}
                      >
                        {l.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <FieldLabel label="Vehicle image (admin-only, ≤500 KB)">
                <div className="flex items-center gap-3">
                  <input type="file" accept="image/*" onChange={(e) => readFile(e.target.files?.[0])} className="text-xs" data-testid="fv-image-upload" />
                  {form.image_data && (
                    <>
                      <img src={form.image_data} alt="" className="w-16 h-12 object-cover rounded border border-black/10" />
                      <button type="button" onClick={() => setForm({ ...form, image_data: "" })} className="text-xs text-rose-600 dark:text-rose-400">Clear</button>
                    </>
                  )}
                </div>
              </FieldLabel>

              <FieldLabel label="Notes">
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="editorial-input w-full" />
              </FieldLabel>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={close} className="border border-black/15 dark:border-white/15 px-6 py-2.5 text-sm rounded">Cancel</button>
                <button type="submit" className="bg-black text-white dark:bg-white dark:text-black px-6 py-2.5 text-sm rounded inline-flex items-center gap-2" data-testid="fv-submit">
                  <Save size={13} /> {editing === "new" ? "Add vehicle" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

function StatusPill({ status }) {
  const label = LEGACY_STATUS[status] || status;
  const tone = {
    "Active":              "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    "At Service":          "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    "At Maintenance":      "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
    "Inactive / For Sale": "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
  }[label] || "bg-neutral-200 text-neutral-700";
  return <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${tone}`}>{label}</span>;
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 block mb-2">{label}</span>
      {children}
    </label>
  );
}
